import { type ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  DescriptionIcon,
  ExternalLinkIcon,
  FeedbackIcon,
  GuideIcon,
  HelpIcon,
  InformationIcon,
  KeyboardIcon,
  PlayIcon,
  WhatsNewIcon,
} from '@dynatrace/strato-icons';
import { getAppId, getAppVersion } from '@dynatrace-sdk/app-environment';
import { sendIntent } from '@dynatrace-sdk/navigation';

import { Button } from '../../buttons/button/Button.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { Menu } from '../../navigation/menu/Menu.js';
import { Tooltip } from '../../overlays/tooltip/Tooltip.js';
import { Flex } from '../flex/Flex.js';

const COMPONENT_NAME = 'HelpMenu';

type MenuItem = {
  label: ReactNode;
  icon: ReactNode;
  key: keyof HelpMenuProps['entries'];
};

const MENU_ITEMS: MenuItem[] = [
  {
    label: (
      <FormattedMessage defaultMessage="What's new" id="TY3F/kE6n91t0TL0" />
    ),
    icon: <WhatsNewIcon />,
    key: 'whatsNew',
  },
  {
    label: (
      <FormattedMessage defaultMessage="Get started" id="kOQNUENAg02eeaTY" />
    ),
    icon: <GuideIcon />,
    key: 'getStarted',
  },
  {
    label: (
      <FormattedMessage defaultMessage="Documentation" id="npzzIhowJGIZhj8d" />
    ),
    icon: <DescriptionIcon />,
    key: 'documentation',
  },
  {
    label: (
      <FormattedMessage
        defaultMessage="Keyboard shortcuts"
        id="JmaVR5qUnuxE3qnb"
      />
    ),
    icon: <KeyboardIcon />,
    key: 'keyboardShortcuts',
  },
  {
    label: (
      <FormattedMessage
        defaultMessage="Try in Playground"
        id="Nar807M4NJhqMw/T"
      />
    ),
    icon: <PlayIcon />,
    key: 'playground',
  },
  {
    label: (
      <FormattedMessage defaultMessage="Share feedback" id="Ia87HCOLBoxMcV9t" />
    ),
    icon: <FeedbackIcon />,
    key: 'feedback',
  },
  {
    label: (
      <FormattedMessage defaultMessage="About this app" id="9/nhbjHpPPAhMbHo" />
    ),
    icon: <InformationIcon />,
    key: 'about',
  },
] as const;

const HelpMenuFooter = () => {
  const version = getAppVersion();
  if (!version) {
    return null;
  }
  return (
    <Menu.Label>
      <div
        style={{
          maxWidth: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        <FormattedMessage
          defaultMessage="Version {version}"
          description="App version label"
          id="W2KEm8AIcM8pOixf"
          values={{ version }}
        />
      </div>
    </Menu.Label>
  );
};

const HelpMenuLink = ({
  href,
  target = '_blank',
  onSelect,
  label,
  icon,
  ...rest
}: {
  href: string;
  target?: string;
  onSelect?: () => void;
  label: ReactNode;
  icon?: ReactNode;
}) => {
  return (
    <Menu.Link
      {...rest}
      onClick={() => onSelect?.()}
      href={href}
      target={target}
    >
      {icon ? <Menu.Prefix>{icon}</Menu.Prefix> : null}
      <Flex justifyContent="space-between" width="inherit">
        {label}
        <ExternalLinkIcon style={{ flexShrink: 0 }} />
      </Flex>
    </Menu.Link>
  );
};

const HelpMenuButton = ({
  onSelect,
  label,
  icon,
  ...rest
}: {
  onSelect: () => void;
  label: ReactNode;
  icon?: ReactNode;
}) => {
  return (
    <Menu.Item onSelect={onSelect} {...rest}>
      {icon ? <Menu.Prefix>{icon}</Menu.Prefix> : null}
      {label}
    </Menu.Item>
  );
};

/** @public */
export type HelpMenuButtonEntry = {
  onSelect: () => void;
};

/** @public */
export type HelpMenuLinkEntry = {
  href: string;
  /** Specify how to open the linked content. */
  target?: string;
  onSelect?: () => void;
};

/**
 * Use the default behavior via app configuration / SDKs
 * @public
 **/
export type HelpMenuDefault = 'default';

/** @public */
export interface HelpMenuCustomLinkEntry extends HelpMenuLinkEntry {
  label: string;
}
/** @public */
export interface HelpMenuCustomItemEntry extends HelpMenuButtonEntry {
  label: string;
}
/** @public */
export type HelpMenuChildrenEntry = Array<
  HelpMenuCustomLinkEntry | HelpMenuCustomItemEntry
>;

/** @public */
export interface HelpMenuProps
  extends WithChildren,
    StylingProps,
    DataTestId,
    MaskingProps {
  entries: {
    whatsNew?: HelpMenuButtonEntry | HelpMenuLinkEntry | HelpMenuDefault;
    getStarted?:
      | HelpMenuButtonEntry
      | HelpMenuLinkEntry
      | HelpMenuChildrenEntry;
    documentation?:
      | HelpMenuButtonEntry
      | HelpMenuLinkEntry
      | HelpMenuChildrenEntry;
    keyboardShortcuts?:
      | HelpMenuButtonEntry
      | HelpMenuLinkEntry
      | HelpMenuDefault;
    playground?: HelpMenuButtonEntry | HelpMenuLinkEntry | HelpMenuDefault;
    feedback?: HelpMenuButtonEntry | HelpMenuLinkEntry | HelpMenuChildrenEntry;
    about?: HelpMenuButtonEntry | HelpMenuLinkEntry | HelpMenuDefault;
  };
  onOpenChange?: (isOpen: boolean) => void;
}

/**
 * A prebuilt menu as entrypoint for app specific help links and actions.
 * @public
 */
export const HelpMenu = (props: HelpMenuProps) => {
  const {
    entries,
    onOpenChange,
    children,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const intl = useIntl();

  const label = intl.formatMessage({
    defaultMessage: 'Help',
    id: 'xHrhU3DP4e0nge7c',
  });

  const handleOpenChange = (value: boolean) => {
    setIsOpen(value);
    onOpenChange?.(value);
  };

  return (
    <Menu
      open={isOpen}
      onOpenChange={handleOpenChange}
      data-dt-component={COMPONENT_NAME}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
    >
      <Tooltip text={label}>
        <Menu.Trigger
          data-dt-component={COMPONENT_NAME}
          data-dt-name="OpenHelpMenu"
        >
          <Button aria-label={label}>
            <Button.Prefix>
              <HelpIcon />
            </Button.Prefix>
          </Button>
        </Menu.Trigger>
      </Tooltip>
      <Menu.Content>
        {MENU_ITEMS.map(({ label, key, icon }) => {
          const entry = entries[key];
          if (!entry) {
            return null;
          }

          if (entry === 'default') {
            return (
              <HelpMenuDefaults
                key={key}
                entryKey={key}
                icon={icon}
                label={label}
              />
            );
          }

          if (Array.isArray(entry)) {
            return (
              <Menu.Sub key={key}>
                <Menu.SubTrigger>
                  <Menu.Prefix>{icon}</Menu.Prefix>
                  {label}
                </Menu.SubTrigger>
                <Menu.SubContent>
                  {entry.map((entryChild) => {
                    if ('href' in entryChild) {
                      return (
                        <HelpMenuLink
                          key={entryChild.label}
                          label={entryChild.label}
                          onSelect={entryChild.onSelect}
                          href={entryChild.href}
                          target={entryChild.target}
                        />
                      );
                    }
                    return (
                      <HelpMenuButton
                        key={entryChild.label}
                        label={entryChild.label}
                        onSelect={entryChild.onSelect}
                      />
                    );
                  })}
                </Menu.SubContent>
              </Menu.Sub>
            );
          }

          if ('href' in entry) {
            return (
              <HelpMenuLink
                key={key}
                label={label}
                icon={icon}
                onSelect={entry.onSelect}
                href={entry.href}
                target={entry.target}
              />
            );
          }
          return (
            <HelpMenuButton
              key={key}
              label={label}
              icon={icon}
              onSelect={entry.onSelect}
            />
          );
        })}
        {children}
        <HelpMenuFooter />
      </Menu.Content>
    </Menu>
  );
};

const HelpMenuDefaults = (props: {
  label: ReactNode;
  entryKey: keyof HelpMenuProps['entries'];
  icon: ReactNode;
}) => {
  switch (props.entryKey) {
    case 'whatsNew':
      return (
        <HelpMenuButton
          onSelect={() =>
            sendIntent(
              {
                appId: getAppId(),
                version: getAppVersion(),
              },
              {
                recommendedAppId: 'dynatrace.hub',
                recommendedIntentId: 'view_app_releases_add_on_intent',
              },
            )
          }
          label={props.label}
          icon={props.icon}
        />
      );
    case 'keyboardShortcuts':
      return (
        <HelpMenuButton
          onSelect={() => {
            // This is a workaround to call the keyboard modal for now.
            // In the future this should be called via an sdk.
            // It is not yet clear yet in which sdk this will be available.
            // If it is available this will be replaced.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (globalThis as any).dtRuntime?.uiCommands?.showUiCommands?.();
          }}
          label={props.label}
          icon={props.icon}
        />
      );
    case 'playground':
      return (
        <HelpMenuLink
          href={`https://wkf10640.apps.dynatrace.com/ui/apps/${getAppId()}`}
          label={props.label}
          icon={props.icon}
        />
      );
    case 'about':
      return (
        <HelpMenuButton
          onSelect={() => {
            sendIntent(
              { appId: getAppId() },
              {
                recommendedAppId: 'dynatrace.hub',
                recommendedIntentId: 'view_app_details',
              },
            );
          }}
          label={props.label}
          icon={props.icon}
        />
      );
    default:
      return null;
  }
};
